import { Eye } from 'lucide-react';
import type { Order } from '../../../api/queries/order.queries';

interface OrdersTableProps {
  orders: Order[];
  onViewDetails: (order: Order) => void;
}

function OrdersTable({ orders, onViewDetails }: OrdersTableProps) {

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'ready':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'picked_up':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'completed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'voided':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getBranchName = (branchId: Order['branchId']) => {
    if (typeof branchId === 'object' && branchId !== null) {
      return `${branchId.name} (${branchId.code})`;
    }
    return branchId;
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-3 font-medium">Order #</th>
              <th className="text-left p-3 font-medium">Date</th>
              <th className="text-left p-3 font-medium">Branch</th>
              <th className="text-center p-3 font-medium">Items</th>
              <th className="text-right p-3 font-medium">Total</th>
              <th className="text-center p-3 font-medium">Payment</th>
              <th className="text-center p-3 font-medium">Status</th>
              <th className="text-center p-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order._id} className="border-t hover:bg-muted/50">
                <td className="p-3">
                  <div className="font-mono font-bold">{order.orderNumber}</div>
                  {order.createdBy && (
                    <div className="text-xs text-muted-foreground">
                      by {typeof order.createdBy === 'object' ? order.createdBy.username : order.createdBy}
                    </div>
                  )}
                </td>
                <td className="p-3">
                  <div className="text-sm">{formatDate(order.createdAt)}</div>
                </td>
                <td className="p-3">
                  <div className="text-sm">{getBranchName(order.branchId)}</div>
                </td>
                <td className="p-3 text-center">
                  <span className="inline-flex items-center justify-center rounded-full bg-muted px-2.5 py-0.5 text-sm font-medium">
                    {order.items.length}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <div className="font-bold">{formatCurrency(order.total)}</div>
                  {order.discount > 0 && (
                    <div className="text-xs text-green-600">
                      Discount: -{formatCurrency(order.discount)}
                    </div>
                  )}
                </td>
                <td className="p-3 text-center">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      order.isPaidInFull
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                    }`}
                  >
                    {order.isPaidInFull ? 'Paid' : 'Pending'}
                  </span>
                </td>
                <td className="p-3 text-center">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusStyle(
                      order.status
                    )}`}
                  >
                    {order.status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </span>
                </td>
                <td className="p-3 text-center">
                  <button
                    onClick={() => onViewDetails(order)}
                    className="inline-flex items-center gap-1 rounded-lg border bg-background px-3 py-1.5 text-sm hover:bg-secondary transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OrdersTable;
